// ./src/pages/private/EventQuestionsPage/questionOptions.ts
//
// A choice question's option rows, as the question drawer edits them — and what happens to them after a save that
// half-failed (ROADMAP #126). No React and no app modules (only types, and OpResult), so `check-drawer-dirty` can run it
// under Node.
import { OpResult } from '@sdflc/api-helpers';

import type { RsvpQuestionOption } from '@/utils/api';
import { runBulkWrites } from '@/utils/bulkWrites';

/** One option row in the drawer; `id` once it exists on the server. */
export interface OptionRow {
  id?: string;
  label: string;
  price: number | null;
}

/**
 * After a save that half-failed: the options that DID save now exist on the server — give each row its id (same label,
 * each server option used once), so Save again updates them instead of creating them a second time.
 */
export const adoptSavedOptionIds = (rows: OptionRow[], saved: readonly RsvpQuestionOption[]): OptionRow[] => {
  const taken = new Set(rows.map((r) => r.id).filter(Boolean));

  return rows.map((row) => {
    if (row.id || !row.label.trim()) {
      return row;
    }

    const match = saved.find((o) => !!o.id && !taken.has(o.id) && (o.label ?? '').trim() === row.label.trim());
    if (!match?.id) {
      return row;
    }

    taken.add(match.id);

    return { ...row, id: match.id };
  });
};

/**
 * Every refusal among a batch of writes — a call that THREW counts as one. Empty when all of them saved. The writes are
 * given as functions that START them, so they go a few at a time and none is sent once the server stops answering
 * (ROADMAP #131 — `runBulkWrites`).
 */
export const refusalsOf = async (ops: Array<() => Promise<OpResult>>): Promise<OpResult[]> => {
  const results = await runBulkWrites(ops, (op) =>
    Promise.resolve()
      .then(op)
      .catch((e: unknown) => OpResult.fromException(e))
  );

  return results.filter((r) => !r.didSucceed());
};
